import React, { useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
	DndContext,
	closestCenter,
	PointerSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	verticalListSortingStrategy,
	arrayMove,
} from "@dnd-kit/sortable";
import {
	FiSliders,
	FiMapPin,
	FiMap,
	FiDownload,
	FiSearch,
	FiSmartphone,
	FiFileText,
	FiX,
	FiLoader,
	FiCheck,
	FiArrowLeft,
} from "react-icons/fi";
import jsPDF from "jspdf";
import SortableStop from "./SortableStop";
import RouteCanvas, { CANVAS_W, getDefaultPosition } from "./RouteCanvas";
import logoCuadrado from "../../assets/logo-cuadrado.png";
import "./RouteBuilder.css";

const TABS = [
	{ id: "design", label: "Diseño", Icon: FiSliders },
	{ id: "places", label: "Lugares", Icon: FiMapPin },
	{ id: "route", label: "Ruta", Icon: FiMap },
	{ id: "download", label: "Descargar", Icon: FiDownload },
];

const ACCENT_COLORS = [
	{ id: "indigo", color: "#6366f1" },
	{ id: "rose", color: "#f43f5e" },
	{ id: "teal", color: "#14b8a6" },
	{ id: "amber", color: "#f59e0b" },
	{ id: "violet", color: "#8b5cf6" },
	{ id: "sky", color: "#0ea5e9" },
];

function RouteBuilder({ cityPack }) {
	const navigate = useNavigate();
	const location = useLocation();
	const handleBack = () => {
		const from = location.state?.from;
		if (
			typeof from === "string" &&
			from.startsWith("/") &&
			from !== "/crear-ruta"
		) {
			navigate(from);
			return;
		}
		if (typeof window !== "undefined" && window.history.length > 1) {
			navigate(-1);
			return;
		}
		navigate("/rutas");
	};
	const [routeState, setRouteState] = useState({
		backgroundId: cityPack.backgrounds[0]?.id || "",
		accentColor: ACCENT_COLORS[0].color,
		title: "",
		dayLabel: "Día 1",
		stops: [],
	});

	const [mobileTab, setMobileTab] = useState(null);
	const [exporting, setExporting] = useState(false);
	const [exportDone, setExportDone] = useState(null);
	const [zoneFilter, setZoneFilter] = useState("Midtown");
	const [searchQuery, setSearchQuery] = useState("");

	const canvasRef = useRef(null);
	const isCustomColor = !ACCENT_COLORS.some((c) => c.color === routeState.accentColor);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(TouchSensor, {
			activationConstraint: { delay: 200, tolerance: 5 },
		})
	);

	/* ── Zones & filtered places ── */

	const zones = useMemo(() => {
		const set = new Set(cityPack.places.map((p) => p.zone));
		return Array.from(set).sort();
	}, [cityPack.places]);

	const filteredPlaces = useMemo(() => {
		if (searchQuery.trim()) {
			const q = searchQuery.toLowerCase().trim();
			return cityPack.places.filter(
				(p) =>
					p.name.toLowerCase().includes(q) ||
					p.zone.toLowerCase().includes(q)
			);
		}
		if (!zoneFilter) return [];
		return cityPack.places.filter((p) => p.zone === zoneFilter);
	}, [cityPack.places, zoneFilter, searchQuery]);

	/* ── Actions ── */

	const set = (updates) => setRouteState((p) => ({ ...p, ...updates }));

	const handleStopMove = useCallback((stopId, x, y) => {
		setRouteState((p) => ({
			...p,
			stops: p.stops.map((s) => (s.id === stopId ? { ...s, x, y } : s)),
		}));
	}, []);

	const addPlace = useCallback((place) => {
		setRouteState((prev) => {
			const pos = getDefaultPosition(prev.stops.length);
			return {
				...prev,
				stops: [
					...prev.stops,
					{
						id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
						type: "place",
						placeId: place.id,
						placeName: place.name,
						time: "",
						note: "",
						x: pos.x,
						y: pos.y,
					},
				],
			};
		});
		setMobileTab("route");
	}, []);

	const addCustomBlock = useCallback((typeId) => {
		setRouteState((prev) => {
			const pos = getDefaultPosition(prev.stops.length);
			return {
				...prev,
				stops: [
					...prev.stops,
					{
						id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
						type: "custom",
						customType: typeId,
						customName: "",
						time: "",
						note: "",
						x: pos.x,
						y: pos.y,
					},
				],
			};
		});
		setMobileTab("route");
	}, []);

	const removeStop = useCallback(
		(id) => setRouteState((p) => ({ ...p, stops: p.stops.filter((s) => s.id !== id) })),
		[]
	);

	const updateStop = useCallback((id, u) => {
		setRouteState((p) => ({
			...p,
			stops: p.stops.map((s) => (s.id === id ? { ...s, ...u } : s)),
		}));
	}, []);

	const handleDragEnd = useCallback((event) => {
		const { active, over } = event;
		if (active.id !== over?.id) {
			setRouteState((p) => {
				const a = p.stops.findIndex((s) => s.id === active.id);
				const b = p.stops.findIndex((s) => s.id === over.id);
				return { ...p, stops: arrayMove(p.stops, a, b) };
			});
		}
	}, []);

	const moveStop = useCallback((id, dir) => {
		setRouteState((p) => {
			const i = p.stops.findIndex((s) => s.id === id);
			if (i < 0) return p;
			const j = i + dir;
			if (j < 0 || j >= p.stops.length) return p;
			return { ...p, stops: arrayMove(p.stops, i, j) };
		});
	}, []);

	const addedPlaceIds = routeState.stops
		.filter((s) => s.type === "place")
		.map((s) => s.placeId);

	/* ── Export ── */

	const doExport = useCallback(
		async (format) => {
			if (!canvasRef.current || exporting) return;
			setExporting(true);
			setExportDone(null);
			try {
				const dataURL = canvasRef.current.toDataURL({
					pixelRatio: 1,
					mimeType: "image/png",
					quality: 0.95,
				});
				if (format === "a5") {
					const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a5" });
					const pageW = pdf.internal.pageSize.getWidth();
					const pageH = pdf.internal.pageSize.getHeight();
					const imgRatio = 1080 / 1920;
					const pageRatio = pageW / pageH;
					let imgW, imgH, imgX, imgY;
					if (imgRatio > pageRatio) {
						imgW = pageW;
						imgH = pageW / imgRatio;
						imgX = 0;
						imgY = (pageH - imgH) / 2;
					} else {
						imgH = pageH;
						imgW = pageH * imgRatio;
						imgX = (pageW - imgW) / 2;
						imgY = 0;
					}
					pdf.setFillColor(255, 255, 255);
					pdf.rect(0, 0, pageW, pageH, "F");
					pdf.addImage(dataURL, "PNG", imgX, imgY, imgW, imgH);
					pdf.save("mi-ruta-viajes-molones.pdf");
				} else {
					const a = document.createElement("a");
					a.download = "mi-ruta-viajes-molones.png";
					a.href = dataURL;
					a.click();
				}
				setExportDone(format);
				setTimeout(() => setExportDone(null), 3000);
			} catch (err) {
				console.error("Export error:", err);
			} finally {
				setExporting(false);
			}
		},
		[exporting]
	);

	/* ── Progress ── */

	const progress = [
		routeState.backgroundId,
		routeState.title.length > 0,
		routeState.stops.length > 0,
	].filter(Boolean).length;

	/* ── Sections ── */

	const renderDesignSection = () => (
		<div className="rb-section">
			<div className="rb-section__header">
				<FiSliders className="rb-section__icon" />
				<span className="rb-section__label">Personaliza tu diseño</span>
			</div>

			<div className="rb-field">
				<span className="rb-field__label">Color de tu ruta</span>
				<p className="rb-field__hint">Se aplica a los números, textos y línea del canvas</p>
				<div className="rb-color-row">
					{ACCENT_COLORS.map((c) => (
						<button
							key={c.id}
							className={`rb-color-dot ${routeState.accentColor === c.color ? "rb-color-dot--active" : ""}`}
							style={{ "--dot-color": c.color }}
							onClick={() => set({ accentColor: c.color })}
							aria-label={c.id}
						/>
					))}
					<label
						className={`rb-color-dot rb-color-dot--custom ${isCustomColor ? "rb-color-dot--active" : ""}`}
						style={{ "--dot-color": isCustomColor ? routeState.accentColor : "#ccc" }}
						title="Elige tu color"
					>
						{!isCustomColor && <span className="rb-color-dot__plus">+</span>}
						<input
							type="color"
							value={routeState.accentColor}
							onChange={(e) => set({ accentColor: e.target.value })}
							className="rb-color-native-input"
							aria-label="Color personalizado"
						/>
					</label>
				</div>
			</div>

			<div className="rb-field">
				<span className="rb-field__label">Fondo</span>
				<div className="rb-bg-grid">
					{cityPack.backgrounds.map((bg) => (
						<button
							key={bg.id}
							className={`rb-bg-card ${routeState.backgroundId === bg.id ? "rb-bg-card--active" : ""}`}
							onClick={() => set({ backgroundId: bg.id })}
							style={
								bg.image
									? { backgroundImage: `url(${bg.image})`, backgroundSize: "cover", backgroundPosition: "center" }
									: { background: bg.gradient }
							}
						>
							{!bg.image && <span className="rb-bg-card__emoji">{bg.emoji}</span>}
							<span className="rb-bg-card__name" style={{ color: bg.image ? "#2c3e50" : bg.text }}>
								{bg.name}
							</span>
							{routeState.backgroundId === bg.id && (
								<div className="rb-bg-card__check">✓</div>
							)}
						</button>
					))}
				</div>
			</div>

			<div className="rb-field">
				<span className="rb-field__label">Título de la ruta</span>
				<input
					className="rb-input"
					value={routeState.title}
					onChange={(e) => set({ title: e.target.value })}
					placeholder="Escribe tu título..."
					maxLength={60}
				/>
				{routeState.title && (
					<span className="rb-char-count">
						{routeState.title.length}/60
					</span>
				)}
			</div>

			<div className="rb-field">
				<span className="rb-field__label">Día o subtítulo</span>
				<input
					className="rb-input rb-input--short"
					value={routeState.dayLabel}
					onChange={(e) => set({ dayLabel: e.target.value })}
					placeholder="Día 1"
					maxLength={30}
				/>
			</div>
		</div>
	);

	const renderPlacesSection = () => (
		<div className="rb-section">
			<div className="rb-section__header">
				<FiMapPin className="rb-section__icon" />
				<span className="rb-section__label">Elige tus paradas</span>
				{addedPlaceIds.length > 0 && (
					<span className="rb-section__badge">
						{addedPlaceIds.length} añadidos
					</span>
				)}
			</div>

			<div className="rb-search">
				<FiSearch className="rb-search__icon" />
				<input
					className="rb-search__input"
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					placeholder="Buscar lugar..."
				/>
				{searchQuery && (
					<button
						className="rb-search__clear"
						onClick={() => setSearchQuery("")}
						aria-label="Limpiar búsqueda"
					>
						<FiX size={12} />
					</button>
				)}
			</div>

			<div className="rb-zone-filter">
				{zones.map((zone) => (
					<button
						key={zone}
						className={`rb-zone-chip ${zoneFilter === zone ? "rb-zone-chip--active" : ""}`}
						onClick={() => setZoneFilter(zoneFilter === zone ? null : zone)}
					>
						{zone}
					</button>
				))}
			</div>

			{filteredPlaces.length === 0 ? (
				<div className="rb-empty rb-empty--sm">
					<p className="rb-empty__sub">
						{!zoneFilter && !searchQuery.trim()
							? "Selecciona una zona para ver los lugares"
							: "No se encontraron lugares"}
					</p>
				</div>
			) : (
				<div className="rb-places-grid">
					{filteredPlaces.map((place) => {
						const added = addedPlaceIds.includes(place.id);
						return (
							<button
								key={place.id}
								className={`rb-place-card ${added ? "rb-place-card--added" : ""}`}
								onClick={() => addPlace(place)}
							>
								{place.image ? (
									<img src={place.image} alt={place.name} className="rb-place-card__img" />
								) : (
									<span className="rb-place-card__emoji">{place.emoji}</span>
								)}
								<span className="rb-place-card__name">{place.name}</span>
								<span className="rb-place-card__zone">{place.zone}</span>
								{added && (
									<span className="rb-place-card__badge">✓</span>
								)}
							</button>
						);
					})}
				</div>
			)}

			<div className="rb-custom-bar">
				<span className="rb-custom-bar__label">¿No está tu lugar? Añade uno:</span>
				<div className="rb-custom-bar__items">
					{cityPack.customBlockTypes.map((t) => (
						<button
							key={t.id}
							className="rb-custom-chip"
							onClick={() => addCustomBlock(t.id)}
						>
							{t.image ? (
								<img src={t.image} alt="" className="rb-custom-chip__img" />
							) : (
								t.emoji
							)}{" "}
							{t.name}
						</button>
					))}
				</div>
			</div>
		</div>
	);

	const renderRouteSection = () => (
		<div className="rb-section">
			<div className="rb-section__header">
				<FiMap className="rb-section__icon" />
				<span className="rb-section__label">Tu recorrido</span>
				{routeState.stops.length > 0 && (
					<span className="rb-section__badge">
						{routeState.stops.length} paradas
					</span>
				)}
			</div>

			{routeState.stops.length === 0 ? (
				<div className="rb-empty">
					<FiMap className="rb-empty__icon-svg" />
					<p className="rb-empty__title">Tu ruta está vacía</p>
					<p className="rb-empty__sub">
						Ve a <strong>Lugares</strong> y toca los sitios que quieres visitar
					</p>
				</div>
			) : (
				<>
					<p className="rb-hint">
						Arrastra o usa ▲ ▼ para cambiar el orden
					</p>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={routeState.stops.map((s) => s.id)}
							strategy={verticalListSortingStrategy}
						>
							<div className="rb-stops-list">
								{routeState.stops.map((stop, idx) => (
									<div key={stop.id} className="rb-stop-wrapper">
										{idx > 0 && <div className="rb-stop-connector" />}
										<SortableStop
											stop={stop}
											order={idx + 1}
											total={routeState.stops.length}
											cityPack={cityPack}
											onRemove={removeStop}
											onUpdate={updateStop}
											onMove={moveStop}
										/>
									</div>
								))}
							</div>
						</SortableContext>
					</DndContext>
				</>
			)}
		</div>
	);

	const renderDownloadSection = () => (
		<div className="rb-section">
			<div className="rb-section__header">
				<FiDownload className="rb-section__icon" />
				<span className="rb-section__label">Descarga tu ruta</span>
			</div>

			{routeState.stops.length === 0 ? (
				<div className="rb-empty rb-empty--sm">
					<p className="rb-empty__sub">Añade paradas para poder descargar</p>
				</div>
			) : (
				<div className="rb-export-actions">
					<button
						className="rb-export-btn rb-export-btn--primary"
						onClick={() => doExport("story")}
						disabled={exporting}
					>
						{exporting ? (
							<><FiLoader className="rb-btn-icon rb-btn-icon--spin" /> Exportando...</>
						) : exportDone === "story" ? (
							<><FiCheck className="rb-btn-icon" /> ¡Descargado!</>
						) : (
							<><FiSmartphone className="rb-btn-icon" /> Descargar para Stories</>
						)}
					</button>
					<button
						className="rb-export-btn rb-export-btn--secondary"
						onClick={() => doExport("a5")}
						disabled={exporting}
					>
						{exportDone === "a5" ? (
							<><FiCheck className="rb-btn-icon" /> ¡Descargado!</>
						) : (
							<><FiFileText className="rb-btn-icon" /> Descargar PDF</>
						)}
					</button>
				</div>
			)}

			{cityPack.cta && (
				<button
					type="button"
					className="rb-cta"
					onClick={() => navigate(cityPack.cta.link || "/")}
				>
					<p className="rb-cta__text">{cityPack.cta.text}</p>
					<p className="rb-cta__sub">{cityPack.cta.subtext}</p>
				</button>
			)}
		</div>
	);

	/* ── Layout ── */

	return (
		<div className="rb-editor">
			<header className="rb-editor__header">
				<div className="rb-editor__header-left">
					<button
						type="button"
						className="rb-header-back"
						onClick={handleBack}
						title="Volver"
						aria-label="Volver"
					>
						<FiArrowLeft size={20} />
					</button>

					<button
						type="button"
						className="rb-header-logo"
						onClick={() => navigate("/")}
						title="Ir al inicio"
						aria-label="Viajes Molones"
					>
						<img src={logoCuadrado} alt="Viajes Molones" />
					</button>

					<div className="rb-header-divider" />

					<div className="rb-header-title-block">
						<span className="rb-header-brand">Viajes Molones</span>
						<div className="rb-header-title-row">
							<div>
								<h1 className="rb-editor__header-title">Crea tu ruta</h1>
								<p className="rb-editor__header-city">{cityPack.name}</p>
							</div>
						</div>
					</div>
				</div>

				<div className="rb-progress">
					{[1, 2, 3].map((s) => (
						<div
							key={s}
							className={`rb-progress__dot ${s <= progress ? "rb-progress__dot--done" : ""}`}
						/>
					))}
					<span className="rb-progress__text">
						{progress === 0 && "Empieza eligiendo un diseño"}
						{progress === 1 && "Ponle un título"}
						{progress === 2 && "Añade lugares"}
						{progress === 3 && "¡Lista para descargar!"}
					</span>
				</div>
			</header>

			<div className="rb-editor__body">
				<div className={`rb-panel ${mobileTab ? "rb-panel--open" : ""}`}>
					<div className="rb-panel__scroll">
						<div className="rb-panel__desktop-sections">
							{renderPlacesSection()}
							{renderRouteSection()}
							{renderDesignSection()}
							{renderDownloadSection()}
						</div>
						<div className="rb-panel__mobile-section">
							{mobileTab === "design" && renderDesignSection()}
							{mobileTab === "places" && renderPlacesSection()}
							{mobileTab === "route" && renderRouteSection()}
							{mobileTab === "download" && renderDownloadSection()}
						</div>
					</div>
				</div>

				{mobileTab && <div className="rb-overlay" onClick={() => setMobileTab(null)} />}

				<div className="rb-canvas">
					<div className="rb-canvas__frame">
						<RouteCanvas
							ref={canvasRef}
							routeState={routeState}
							cityPack={cityPack}
							onStopMove={handleStopMove}
						/>
					</div>
				</div>
			</div>

			<div className="rb-tabbar">
				{TABS.map((tab) => (
					<button
						key={tab.id}
						className={`rb-tabbar__btn ${mobileTab === tab.id ? "rb-tabbar__btn--active" : ""}`}
						onClick={() => setMobileTab(mobileTab === tab.id ? null : tab.id)}
					>
						<tab.Icon className="rb-tabbar__icon" />
						<span className="rb-tabbar__label">{tab.label}</span>
						{tab.id === "route" && routeState.stops.length > 0 && (
							<span className="rb-tabbar__badge">
								{routeState.stops.length}
							</span>
						)}
					</button>
				))}
			</div>
		</div>
	);
}

export default RouteBuilder;
